import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  CssBaseline,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import BlockIcon from "@mui/icons-material/Block";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import moment from "moment";
import swal from "sweetalert";
import Navbar from "../../Navbar";
import OrdenPagoStepper from "./OrdenPagoStepper";
import TabSeleccionEmpresa, {
  idDeEmpresa,
  nombreDeEmpresa,
} from "./TabSeleccionEmpresa";
import TabMedioPago from "./TabMedioPago";
import { nuevaFila } from "./SeleccionCuentasOrigen";
import TabConfirmacion from "./TabConfirmacion";
import ResultadoGeneracionMultiOp from "./ResultadoGeneracionMultiOp";
import ModalConfirmarOrdenPago from "./ModalConfirmarOrdenPago";
import ModalAnularOrdenPago from "./ModalAnularOrdenPago";
import ModalCrearAdelanto from "./ModalCrearAdelanto";
import ModalRetencionExcedePago from "./ModalRetencionExcedePago";
import ListadoPendientesEmpresa, {
  cuitProveedor,
} from "./ListadoPendientesEmpresa";
import {
  generarOrdenPago,
  actualizarOrdenPago,
  anularOrdenPago,
  aprobarOrdenPago,
  crearAdelantoProveedor,
  crearLoteOrdenPago,
  getBancosCuentasActivos,
  getEmpresas,
  getImputacionesGastos,
  getPendientesEmpresa,
  normalizarCuit,
  previewLoteOrdenPago,
  previewRetenciones,
} from "./ApiServices";
import {
  escalarMedioPagoATotal,
  enriquecerCuentasOrigen,
  agruparFilasPorCuit,
  ajustarImportesAdelantos,
  buildGenerarOrdenPagoBody,
  buildLoteOrdenPagoBody,
  buildLotePreviewBody,
  buildPreviewAdelantosLocal,
  esDocumentoAdelanto,
  esGeneracionMultiOp,
  esPagoNoProveedor,
  esRecargaPagina,
  hayMedioActivo,
  idDocumentoPendiente,
  importeEsValido,
  mapFilaAComprobanteBody,
  mapFilasAPagosNoProveedorBody,
  mapImputacionAFilaComprobante,
  MENSAJE_ERROR_IMPORTES,
  mensajeErrorAnularOp,
  mensajeErrorChequeDuplicadoLote,
  mensajeErrorLote,
  mensajeLoteGenerado,
  mensajeOrdenGenerada,
  mergeAdelantosEnPreview,
  mergePagosNoProveedorEnPreview,
  mergePreviewsProveedores,
  medioPagoInicial as crearMedioPagoInicial,
  normalizarMensajeErrorPreview,
  obtenerComprobantesPreview,
  obtenerResumenPreview,
  previewRequiereConfirmacion,
  previewTieneResumen,
  excedenteRetencionPreview,
  requiereMediosPago,
  separarGruposProveedorYPagosVarios,
  sumaImportesMedios,
  validarMedioPagoMulti,
  etiquetaEstadoOp,
  armarMedioPagoAgregadoDesdeConfigs,
  armarMedioPagoDesdeConfigProveedor,
  armarMediosPagoBodySafe,
  cantidadChequesMedio,
  configPagoProveedorVacia,
  configsPagoSeleccionCompletas,
  editarRetencionPreviewProveedor,
  esMedioChequeProv,
  retencionesBodyDesdeGrupo,
} from "./utils";
import {
  COLOR_EMPRESA_DEFAULT,
  colorDeEmpresa,
} from "./coloresEmpresas";

const PASO = {
  EMPRESA: 0,
  COMPROBANTES: 1,
  MEDIO: 2,
  CONFIRMACION: 3,
};

const filtrosPendientesIniciales = () => ({
  vto_desde: "",
  vto_hasta: "",
  cuit: "",
  id_imputacion: "",
});

const crearTemaPagos = (colorPrimary = COLOR_EMPRESA_DEFAULT) =>
  createTheme({
    palette: { mode: "light", primary: { main: colorPrimary } },
  });

const medioPagoInicial = () => crearMedioPagoInicial(moment());

const estadoLimpio = () => ({
  activeStep: 0,
  cuit: "",
  error: "",
  resultado: null,
  rowSelection: {},
  importesAPagar: {},
  preview: null,
  previewError: "",
  medioPago: medioPagoInicial(),
});

const aplanarDocumentosPendientes = (proveedores = []) =>
  proveedores.flatMap((prov) =>
    (prov.documentos ?? []).map((doc) => ({
      ...doc,
      id_proveedor: prov.id_proveedor,
      razon_social: prov.razon_social,
      nro_cuit: prov.nro_cuit,
      cbu: prov.cbu,
      id_banco: prov.id_banco,
      banco_nombre: prov.banco_nombre,
      id_empresa: doc.id_empresa,
      nombre_empresa: doc.nombre_empresa,
      es_pagos_no_proveedor: Boolean(prov.es_pagos_no_proveedor),
      es_pago_no_proveedor:
        Boolean(doc.es_pago_no_proveedor) ||
        Boolean(prov.es_pagos_no_proveedor) ||
        doc.id_pago_no_proveedor != null,
      saldo_pendiente: doc.saldo_pendiente ?? doc.monto,
      importe_a_pagar:
        doc.importe_a_pagar != null
          ? doc.importe_a_pagar
          : doc.saldo_pendiente ?? doc.monto,
    }))
  );

const puedeRestaurarSesion = (estadoInicial, initialStep) =>
  Boolean(estadoInicial?.modoEdicion) ||
  (!esRecargaPagina() &&
    initialStep > 0 &&
    previewTieneResumen(estadoInicial?.preview));

const OrdenPagoWizard = ({ initialStep = 0, estadoInicial = null }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const modoEdicion = Boolean(estadoInicial?.modoEdicion);
  const idOrdenPagoEdicion = estadoInicial?.idOrdenPagoEdicion ?? null;
  const nroOpEdicion = estadoInicial?.nroOpEdicion ?? null;
  const restaurar = puedeRestaurarSesion(estadoInicial, initialStep);
  const inicio = restaurar
    ? {
        activeStep: initialStep,
        cuit: estadoInicial.cuitConsultado ?? "",
        resultado: estadoInicial.resultado ?? null,
        rowSelection: estadoInicial.rowSelection ?? {},
        importesAPagar: estadoInicial.importesAPagar ?? {},
        preview: estadoInicial.preview ?? null,
        medioPago: estadoInicial.medioPago ?? medioPagoInicial(),
      }
    : estadoLimpio();

  const [activeStep, setActiveStep] = useState(inicio.activeStep);
  const [cuit, setCuit] = useState(inicio.cuit);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [resultado, setResultado] = useState(inicio.resultado);
  const [rowSelection, setRowSelection] = useState(inicio.rowSelection);
  const [importesAPagar, setImportesAPagar] = useState(inicio.importesAPagar);
  const [preview, setPreview] = useState(inicio.preview);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState("");
  const [previewMediosLote, setPreviewMediosLote] = useState(null);
  const [previewMediosLoading, setPreviewMediosLoading] = useState(false);
  const [previewMediosError, setPreviewMediosError] = useState("");
  const [medioPago, setMedioPago] = useState(inicio.medioPago);
  const [configPagoPorProveedor, setConfigPagoPorProveedor] = useState(
    estadoInicial?.configPagoPorProveedor ?? {}
  );
  const [cuentasBancarias, setCuentasBancarias] = useState([]);
  const [loadingCuentas, setLoadingCuentas] = useState(false);
  const [generando, setGenerando] = useState(false);
  const [resultadoGeneracion, setResultadoGeneracion] = useState(null);
  const [dialogMultiOp, setDialogMultiOp] = useState(false);
  const [modalConfirmarOpen, setModalConfirmarOpen] = useState(false);
  const [confirmacionRetencion, setConfirmacionRetencion] = useState(null);
  // Proveedor que hizo fallar la creación del lote (respuesta proveedor_error).
  const [proveedorConError, setProveedorConError] = useState(null);
  const [modalAdelantoOpen, setModalAdelantoOpen] = useState(false);
  const [generandoAdelanto, setGenerandoAdelanto] = useState(false);
  const [modalAnularOpen, setModalAnularOpen] = useState(false);
  const [anulando, setAnulando] = useState(false);
  const [empresas, setEmpresas] = useState([]);
  const [loadingEmpresas, setLoadingEmpresas] = useState(true);
  const [errorEmpresas, setErrorEmpresas] = useState("");
  const [idEmpresaSeleccionada, setIdEmpresaSeleccionada] = useState(
    estadoInicial?.idEmpresaSeleccionada ?? null
  );
  const [nombreEmpresaSeleccionada, setNombreEmpresaSeleccionada] = useState(
    estadoInicial?.nombreEmpresaSeleccionada ?? ""
  );
  const [filtrosPendientes, setFiltrosPendientes] = useState(
    filtrosPendientesIniciales
  );
  const [filtrosAplicados, setFiltrosAplicados] = useState(
    filtrosPendientesIniciales
  );
  const [imputaciones, setImputaciones] = useState([]);

  const colorEmpresa = useMemo(
    () => colorDeEmpresa(idEmpresaSeleccionada),
    [idEmpresaSeleccionada]
  );
  const temaPagos = useMemo(() => crearTemaPagos(colorEmpresa), [colorEmpresa]);

  const limpiarEstadoProveedor = useCallback(() => {
    setRowSelection({});
    setImportesAPagar({});
    setPreview(null);
    setPreviewError("");
    setConfigPagoPorProveedor({});
    setMedioPago({
      ...medioPagoInicial(),
      transferencia: { activo: false, cuentasOrigen: [] },
    });
    setPreviewLoading(false);
  }, []);

  /** Vuelve al paso Empresa con el flujo en cero (sin proveedores ni medios). */
  const volverASeleccionEmpresa = useCallback(() => {
    setActiveStep(PASO.EMPRESA);
    setCuit("");
    setError("");
    setResultado(null);
    setRowSelection({});
    setImportesAPagar({});
    setPreview(null);
    setPreviewError("");
    setConfigPagoPorProveedor({});
    setMedioPago({
      ...medioPagoInicial(),
      transferencia: { activo: false, cuentasOrigen: [] },
    });
    setPreviewLoading(false);
    setLoading(false);
    setLoadingCuentas(false);
    setCuentasBancarias([]);
    setModalConfirmarOpen(false);
    setConfirmacionRetencion(null);
    setProveedorConError(null);
    setModalAdelantoOpen(false);
    setIdEmpresaSeleccionada(null);
    setNombreEmpresaSeleccionada("");
    setFiltrosPendientes(filtrosPendientesIniciales());
    setFiltrosAplicados(filtrosPendientesIniciales());
  }, []);

  /**
   * Red de seguridad: al estar en el paso Empresa (volviendo de otro paso o
   * al montar), no debe quedar selección de comprobantes, preview ni medios.
   */
  const pasoAnteriorRef = useRef(activeStep);
  useEffect(() => {
    if (modoEdicion) return;

    const pasoAnterior = pasoAnteriorRef.current;
    pasoAnteriorRef.current = activeStep;

    if (activeStep !== PASO.EMPRESA) return;
    // Solo al llegar al paso Empresa desde un paso posterior.
    if (!(pasoAnterior > PASO.EMPRESA)) return;

    setCuit("");
    setError("");
    setResultado(null);
    setRowSelection({});
    setImportesAPagar({});
    setPreview(null);
    setPreviewError("");
    setConfigPagoPorProveedor({});
    setMedioPago({
      ...medioPagoInicial(),
      transferencia: { activo: false, cuentasOrigen: [] },
    });
    setPreviewLoading(false);
    setLoading(false);
    setLoadingCuentas(false);
    setCuentasBancarias([]);
    setModalConfirmarOpen(false);
    setConfirmacionRetencion(null);
    setProveedorConError(null);
    setModalAdelantoOpen(false);
    setIdEmpresaSeleccionada(null);
    setNombreEmpresaSeleccionada("");
    setFiltrosPendientes(filtrosPendientesIniciales());
    setFiltrosAplicados(filtrosPendientesIniciales());
  }, [activeStep, modoEdicion]);

  const reiniciarWizard = useCallback(() => {
    const limpio = estadoLimpio();
    setActiveStep(limpio.activeStep);
    setCuit(limpio.cuit);
    setError(limpio.error);
    setResultado(limpio.resultado);
    setRowSelection(limpio.rowSelection);
    setImportesAPagar(limpio.importesAPagar);
    setPreview(limpio.preview);
    setPreviewError(limpio.previewError);
    setConfigPagoPorProveedor({});
    setMedioPago(limpio.medioPago);
    setLoading(false);
    setPreviewLoading(false);
    setLoadingCuentas(false);
    setCuentasBancarias([]);
    setGenerando(false);
    setResultadoGeneracion(null);
    setDialogMultiOp(false);
    setModalConfirmarOpen(false);
    setConfirmacionRetencion(null);
    setProveedorConError(null);
    setIdEmpresaSeleccionada(null);
    setNombreEmpresaSeleccionada("");
    setFiltrosPendientes(filtrosPendientesIniciales());
    setFiltrosAplicados(filtrosPendientesIniciales());
  }, []);

  useEffect(() => {
    let cancelado = false;
    setLoadingEmpresas(true);
    setErrorEmpresas("");
    getEmpresas()
      .then((data) => {
        if (!cancelado) setEmpresas(Array.isArray(data) ? data : []);
      })
      .catch((err) => {
        if (!cancelado) {
          setEmpresas([]);
          setErrorEmpresas(err.message || "Error al cargar empresas");
        }
      })
      .finally(() => {
        if (!cancelado) setLoadingEmpresas(false);
      });
    getImputacionesGastos()
      .then((data) => {
        if (!cancelado) setImputaciones(Array.isArray(data) ? data : []);
      })
      .catch(() => {
        if (!cancelado) setImputaciones([]);
      });
    return () => {
      cancelado = true;
    };
  }, []);

  useEffect(() => {
    if (modoEdicion) return;
    if (esRecargaPagina() && location.pathname.includes("/orden")) {
      navigate("/pago_proveedores", { replace: true });
    }
  }, [location.pathname, navigate, modoEdicion]);

  useEffect(() => {
    if (modoEdicion) return;
    if (activeStep > PASO.COMPROBANTES && !previewTieneResumen(preview)) {
      const limpio = estadoLimpio();
      setActiveStep(limpio.activeStep);
      setCuit(limpio.cuit);
      setResultado(limpio.resultado);
      setRowSelection(limpio.rowSelection);
      setImportesAPagar(limpio.importesAPagar);
      setPreview(limpio.preview);
      setPreviewError(limpio.previewError);
      setConfigPagoPorProveedor({});
      setMedioPago(limpio.medioPago);
      setError(limpio.error);
      setIdEmpresaSeleccionada(null);
      setNombreEmpresaSeleccionada("");
    }
  }, [activeStep, preview, modoEdicion]);

  const cargarPendientes = useCallback(
    async (filtros = filtrosAplicados) => {
      if (modoEdicion) return;
      if (idEmpresaSeleccionada == null || idEmpresaSeleccionada === "") {
        return;
      }
      setLoading(true);
      setError("");
      try {
        const data = await getPendientesEmpresa({
          id_empresa: idEmpresaSeleccionada,
          vto_desde: filtros.vto_desde || undefined,
          vto_hasta: filtros.vto_hasta || undefined,
          cuit: filtros.cuit || undefined,
          id_imputacion:
            filtros.id_imputacion !== "" && filtros.id_imputacion != null
              ? filtros.id_imputacion
              : undefined,
        });
        setResultado(data);
        limpiarEstadoProveedor();
      } catch (err) {
        setResultado(null);
        setError(err.message || "Error al cargar comprobantes pendientes");
      } finally {
        setLoading(false);
      }
    },
    [idEmpresaSeleccionada, filtrosAplicados, limpiarEstadoProveedor, modoEdicion]
  );

  useEffect(() => {
    if (modoEdicion) return;
    if (activeStep !== PASO.COMPROBANTES) return;
    if (idEmpresaSeleccionada == null || idEmpresaSeleccionada === "") return;

    const f = resultado?.filtros;
    const yaCargado =
      Array.isArray(resultado?.proveedores) &&
      f &&
      String(f.id_empresa) === String(idEmpresaSeleccionada) &&
      String(f.vto_desde || "") === String(filtrosAplicados.vto_desde || "") &&
      String(f.vto_hasta || "") === String(filtrosAplicados.vto_hasta || "") &&
      String(normalizarCuit(f.cuit) || "") ===
        String(normalizarCuit(filtrosAplicados.cuit) || "") &&
      String(f.id_imputacion ?? "") ===
        String(
          filtrosAplicados.id_imputacion === "" ||
            filtrosAplicados.id_imputacion == null
            ? ""
            : filtrosAplicados.id_imputacion
        );

    if (yaCargado) return;

    cargarPendientes(filtrosAplicados);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeStep, idEmpresaSeleccionada, filtrosAplicados, modoEdicion]);

  const proveedores = resultado?.proveedores ?? [];
  const documentos = useMemo(() => {
    if (Array.isArray(resultado?.proveedores) && resultado.proveedores.length > 0) {
      return aplanarDocumentosPendientes(resultado.proveedores);
    }
    return resultado?.documentos ?? [];
  }, [resultado]);
  const fechaHoy = moment().format("DD/MM/YYYY");

  const filasSeleccionadas = useMemo(
    () =>
      documentos.filter((doc) => {
        const id = idDocumentoPendiente(doc);
        return id && rowSelection[id];
      }),
    [documentos, rowSelection]
  );

  const cuitConsultado = useMemo(() => {
    if (filasSeleccionadas.length > 0) {
      const fromDoc = cuitProveedor(filasSeleccionadas[0]);
      if (fromDoc) return fromDoc;
    }
    return normalizarCuit(cuit);
  }, [filasSeleccionadas, cuit]);

  const proveedoresSeleccionados = useMemo(
    () => agruparFilasPorCuit(filasSeleccionadas),
    [filasSeleccionadas]
  );

  const { proveedoresReales, gruposPagosVarios } = useMemo(
    () => separarGruposProveedorYPagosVarios(proveedoresSeleccionados),
    [proveedoresSeleccionados]
  );

  const filasPagosNoProveedor = useMemo(
    () => filasSeleccionadas.filter(esPagoNoProveedor),
    [filasSeleccionadas]
  );

  const pagosNoProveedorBody = useMemo(
    () => mapFilasAPagosNoProveedorBody(filasPagosNoProveedor),
    [filasPagosNoProveedor]
  );

  const cantidadProveedoresSeleccionados = proveedoresReales.length;
  const soloPagosVarios =
    proveedoresReales.length === 0 && filasPagosNoProveedor.length > 0;
  const debeUsarLote =
    !(proveedoresReales.length === 1 && pagosNoProveedorBody.length === 0) &&
    (proveedoresReales.length > 0 || pagosNoProveedorBody.length > 0);

  const razonSocial = useMemo(() => {
    if (soloPagosVarios) {
      return (
        gruposPagosVarios[0]?.razon_social ||
        "Pagos varios / Impuestos y conceptos"
      );
    }
    if (cantidadProveedoresSeleccionados === 1) {
      const base = proveedoresReales[0].razon_social || "";
      if (pagosNoProveedorBody.length) {
        return `${base} + pagos varios`;
      }
      return base;
    }
    if (cantidadProveedoresSeleccionados > 1) {
      const extra = pagosNoProveedorBody.length
        ? ` + ${pagosNoProveedorBody.length} pagos varios`
        : "";
      return `${cantidadProveedoresSeleccionados} proveedores${extra}`;
    }
    return (
      resultado?.razon_social ||
      resultado?.proveedor?.razon_social ||
      ""
    );
  }, [
    soloPagosVarios,
    gruposPagosVarios,
    proveedoresReales,
    cantidadProveedoresSeleccionados,
    pagosNoProveedorBody.length,
    resultado,
  ]);

  const comprobantesDetalle = useMemo(
    () =>
      filasSeleccionadas.map((row) => {
        if (esPagoNoProveedor(row)) {
          return {
            ...row,
            es_pago_no_proveedor: true,
            id_pago_no_proveedor:
              row.id_pago_no_proveedor ?? row.id ?? null,
            tipo_comprobante:
              row.concepto || row.tipo_comprobante || "Pago varios",
            tipo_comprobante_abrev: row.tipo_comprobante_abrev,
            nro_comprobante:
              row.motivo ||
              row.nro_comprobante ||
              (row.id_pago_no_proveedor != null
                ? String(row.id_pago_no_proveedor)
                : "—"),
            fecha_factura: row.fecha_factura || row.fecha || null,
            fecha_vencimiento: row.fecha_vencimiento || row.fecha || null,
            total: row.total ?? row.monto,
            saldo_pendiente: row.saldo_pendiente ?? row.monto,
            importe_a_pagar: row.saldo_pendiente ?? row.monto,
            id_empresa: row.id_empresa ?? idEmpresaSeleccionada,
            nombre_empresa: row.nombre_empresa ?? nombreEmpresaSeleccionada,
            razon_social: row.razon_social,
            nro_cuit: row.nro_cuit,
          };
        }

        return {
          idcomprobante: row.idcomprobante,
          tipo_comprobante: row.tipo_comprobante,
          tipo_comprobante_abrev: row.tipo_comprobante_abrev,
          nro_comprobante: row.nro_comprobante,
          fecha_factura: row.fecha_factura,
          fecha_vencimiento: row.fecha_vencimiento,
          total: row.total,
          saldo_pendiente: row.saldo_pendiente,
          debito: row.debito,
          credito: row.credito,
          es_adelanto: row.es_adelanto,
          id_anticipo: row.id_anticipo,
          id_orden_pago: row.id_orden_pago,
          id_empresa: row.id_empresa ?? idEmpresaSeleccionada,
          nombre_empresa: row.nombre_empresa ?? nombreEmpresaSeleccionada,
          razon_social: row.razon_social,
          nro_cuit: row.nro_cuit,
        };
      }),
    [
      filasSeleccionadas,
      idEmpresaSeleccionada,
      nombreEmpresaSeleccionada,
      importesAPagar,
    ]
  );

  const comprobantesTabla = useMemo(() => {
    const idsSeleccionados = new Set(
      filasSeleccionadas
        .map((d) => idDocumentoPendiente(d))
        .filter(Boolean)
        .map(String)
    );
    const delPreview = obtenerComprobantesPreview(preview);
    const mapDetalle = new Map(
      comprobantesDetalle
        .filter((c) => !esPagoNoProveedor(c))
        .map((c) => [String(idDocumentoPendiente(c)), c])
        .filter(([id]) => id && id !== "null" && id !== "undefined")
    );

    const filasProveedor =
      delPreview.length > 0
        ? delPreview.map((c) => {
            const fila = mapImputacionAFilaComprobante(c);
            const id = String(
              idDocumentoPendiente(fila) ||
                idDocumentoPendiente(c) ||
                c.idcomprobante ||
                ""
            );
            const detalle = mapDetalle.get(id);
            return {
              ...fila,
              ...detalle,
              nro_cuit: c.nro_cuit ?? detalle?.nro_cuit ?? fila.nro_cuit,
              razon_social:
                c.razon_social ?? detalle?.razon_social ?? fila.razon_social,
            };
          })
        : comprobantesDetalle
            .filter((c) => !esPagoNoProveedor(c))
            .map((c) => ({
              ...c,
              importe_a_pagar:
                importesAPagar[idDocumentoPendiente(c)] ??
                c.importe_a_pagar ??
                c.saldo_pendiente,
            }));

    const filasPnp = comprobantesDetalle.filter(esPagoNoProveedor);

    const filas = [...filasProveedor, ...filasPnp];

    // Mientras llega el preview nuevo, no mostrar comprobantes ya deseleccionados.
    if (idsSeleccionados.size > 0) {
      return filas.filter((c) => {
        const id = idDocumentoPendiente(c);
        return id && idsSeleccionados.has(String(id));
      });
    }
    return filas;
  }, [preview, comprobantesDetalle, importesAPagar, filasSeleccionadas]);

  const errorImportes = useMemo(() => {
    if (filasSeleccionadas.length === 0) return "";
    const invalido = filasSeleccionadas.some((row) => {
      const id = idDocumentoPendiente(row);
      const saldo = row.saldo_pendiente ?? row.monto;
      const importe = importesAPagar[id] ?? saldo;
      return !importeEsValido(importe, saldo, row);
    });
    if (invalido) {
      return MENSAJE_ERROR_IMPORTES;
    }
    return "";
  }, [filasSeleccionadas, importesAPagar]);

  /** Actualiza importes y topea adelantos a lo necesario para cubrir facturas. */
  const handleImportesAPagarChange = useCallback(
    (updater) => {
      setImportesAPagar((prev) => {
        const draft =
          typeof updater === "function" ? updater(prev) : { ...updater };
        const filas = documentos.filter((doc) => {
          const id = idDocumentoPendiente(doc);
          return id && rowSelection[id];
        });
        const { importes, deseleccionar } = ajustarImportesAdelantos(
          filas,
          draft
        );

        if (deseleccionar.length) {
          queueMicrotask(() => {
            setRowSelection((selPrev) => {
              let changed = false;
              const selNext = { ...selPrev };
              deseleccionar.forEach((id) => {
                if (selNext[id]) {
                  delete selNext[id];
                  changed = true;
                }
              });
              return changed ? selNext : selPrev;
            });
          });
        }

        const prevKeys = Object.keys(prev);
        const nextKeys = Object.keys(importes);
        if (
          prevKeys.length === nextKeys.length &&
          nextKeys.every((k) => String(prev[k]) === String(importes[k]))
        ) {
          return prev;
        }
        return importes;
      });
    },
    [documentos, rowSelection]
  );

  /** Quita un comprobante de la selección (paso Medio de pago). */
  const handleQuitarComprobante = useCallback((idDoc) => {
    const id = String(idDoc || "");
    if (!id) return;
    setRowSelection((prev) => {
      const seleccionados = Object.keys(prev).filter((k) => prev[k]);
      if (seleccionados.length <= 1) return prev;
      if (!prev[id]) return prev;
      const next = { ...prev };
      delete next[id];
      return next;
    });
    setImportesAPagar((prev) => {
      if (prev[id] == null) return prev;
      const next = { ...prev };
      delete next[id];
      return next;
    });
  }, []);

  useEffect(() => {
    // Sin selección: limpiar importes (como OP en cero).
    if (!Object.values(rowSelection).some(Boolean)) {
      setImportesAPagar((prev) => (Object.keys(prev).length ? {} : prev));
      return;
    }

    setImportesAPagar((prev) => {
      const next = {};
      const filas = [];
      documentos.forEach((doc) => {
        const id = idDocumentoPendiente(doc);
        if (!id || !rowSelection[id]) return;
        filas.push(doc);
        const prevVal = prev[id];
        next[id] = esPagoNoProveedor(doc)
          ? Number(doc.saldo_pendiente ?? doc.monto)
          : prevVal == null || prevVal === ""
            ? Number(doc.saldo_pendiente)
            : prevVal;
      });

      const { importes, deseleccionar } = ajustarImportesAdelantos(filas, next);

      if (deseleccionar.length) {
        queueMicrotask(() => {
          setRowSelection((selPrev) => {
            let changed = false;
            const selNext = { ...selPrev };
            deseleccionar.forEach((id) => {
              if (selNext[id]) {
                delete selNext[id];
                changed = true;
              }
            });
            return changed ? selNext : selPrev;
          });
        });
      }

      const prevKeys = Object.keys(prev);
      const nextKeys = Object.keys(importes);
      if (
        prevKeys.length === nextKeys.length &&
        nextKeys.every((k) => String(prev[k]) === String(importes[k]))
      ) {
        return prev;
      }
      return importes;
    });
  }, [rowSelection, documentos]);

  useEffect(() => {
    let activo = true;

    if (filasSeleccionadas.length === 0 || proveedoresSeleccionados.length === 0) {
      setPreview(null);
      setPreviewError("");
      setPreviewLoading(false);
      return () => {
        activo = false;
      };
    }

    if (errorImportes) {
      setPreview(null);
      setPreviewError(errorImportes);
      setPreviewLoading(false);
      return () => {
        activo = false;
      };
    }

    setPreviewLoading(true);

    const timer = window.setTimeout(async () => {
      if (!activo) return;
      setPreviewError("");

      try {
        const gruposReales = proveedoresSeleccionados.filter(
          (g) => !g.es_pagos_no_proveedor
        );
        const filasPnp = filasSeleccionadas.filter(esPagoNoProveedor);

        let merged = null;
        if (gruposReales.length > 0) {
          const items = await Promise.all(
            gruposReales.map(async (grupo) => {
              const facturas = grupo.documentos.filter(
                (row) => !esDocumentoAdelanto(row) && !esPagoNoProveedor(row)
              );
              const adelantos = grupo.documentos.filter(esDocumentoAdelanto);

              let previewProv;
              if (facturas.length > 0) {
                const comprobantes = facturas.map((row) =>
                  mapFilaAComprobanteBody(row, importesAPagar)
                );
                // En edición, devolver el cupo de la OP (saldo + reserva actual).
                previewProv = await previewRetenciones(grupo.cuit, {
                  comprobantes,
                  ...(modoEdicion && idOrdenPagoEdicion
                    ? { id_orden_pago_edicion: Number(idOrdenPagoEdicion) }
                    : {}),
                });
                previewProv = mergeAdelantosEnPreview(
                  previewProv,
                  adelantos,
                  importesAPagar
                );
              } else {
                previewProv = buildPreviewAdelantosLocal(
                  adelantos,
                  importesAPagar
                );
              }

              return {
                cuit: grupo.cuit,
                razon_social: grupo.razon_social,
                preview: previewProv,
              };
            })
          );
          if (!activo) return;
          merged = mergePreviewsProveedores(items);
        }

        if (!activo) return;
        setPreview(
          mergePagosNoProveedorEnPreview(merged, filasPnp, importesAPagar)
        );
      } catch (err) {
        if (!activo) return;
        setPreview(null);
        setPreviewError(
          normalizarMensajeErrorPreview(
            err.message || "Error al calcular retenciones"
          )
        );
      } finally {
        if (activo) {
          setPreviewLoading(false);
        }
      }
    }, 400);

    return () => {
      activo = false;
      window.clearTimeout(timer);
    };
  }, [
    filasSeleccionadas,
    proveedoresSeleccionados,
    importesAPagar,
    errorImportes,
    modoEdicion,
    idOrdenPagoEdicion,
  ]);

  useEffect(() => {
    if (modoEdicion || activeStep >= PASO.COMPROBANTES) {
      setLoadingCuentas(true);
      getBancosCuentasActivos()
        .then(setCuentasBancarias)
        .catch(() => setCuentasBancarias([]))
        .finally(() => setLoadingCuentas(false));
    }
  }, [activeStep, modoEdicion]);

  // Quitar configs de proveedores que ya no tienen comprobantes seleccionados.
  useEffect(() => {
    const cuitsActivos = new Set(
      proveedoresReales.map((g) => String(g.cuit))
    );
    setConfigPagoPorProveedor((prev) => {
      const keys = Object.keys(prev || {});
      if (!keys.length) return prev;
      let cambio = false;
      const next = {};
      keys.forEach((cuit) => {
        if (cuitsActivos.has(String(cuit))) {
          next[cuit] = prev[cuit];
        } else {
          cambio = true;
        }
      });
      return cambio ? next : prev;
    });
  }, [proveedoresReales]);

  const configsPagoOk = useMemo(() => {
    if (modoEdicion) return true;
    if (soloPagosVarios) return true;
    if (!proveedoresReales.length) return false;
    const proveedoresPorCuit = Object.fromEntries(
      proveedoresReales.map((g) => [String(g.cuit), g])
    );
    return configsPagoSeleccionCompletas(
      proveedoresReales.map((g) => g.cuit),
      configPagoPorProveedor,
      proveedoresPorCuit
    );
  }, [modoEdicion, soloPagosVarios, proveedoresReales, configPagoPorProveedor]);

  const handleConfigPagoProveedorChange = useCallback((cuit, next) => {
    setConfigPagoPorProveedor((prev) => ({
      ...prev,
      [String(cuit)]: {
        ...(prev?.[String(cuit)] || configPagoProveedorVacia()),
        ...next,
      },
    }));
    setProveedorConError((prev) =>
      prev && prev.cuit === String(cuit) ? null : prev
    );
  }, []);

  const handleEditarRetencion = useCallback((cuit, clave, cambios) => {
    setPreview((prev) =>
      editarRetencionPreviewProveedor(prev, cuit, clave, cambios)
    );
  }, []);

  const puedeAvanzarComprobantes =
    filasSeleccionadas.length > 0 &&
    !errorImportes &&
    !previewLoading &&
    !previewError &&
    previewTieneResumen(preview) &&
    configsPagoOk;

  const totalAPagarRaw = obtenerResumenPreview(preview)?.total_a_pagar;
  const totalAPagar =
    totalAPagarRaw != null && totalAPagarRaw !== ""
      ? Number(totalAPagarRaw)
      : null;

  /** Proveedores con su total a pagar, para asignar cheques cuando hay varias OPs. */
  const proveedoresConTotal = useMemo(
    () =>
      proveedoresReales.map((grupo) => {
        const previewGrupo =
          preview?.grupos?.find((g) => String(g.cuit) === String(grupo.cuit)) ??
          null;
        const total =
          Number(
            previewGrupo?.resumen?.total_a_pagar ??
              (proveedoresReales.length === 1 && !pagosNoProveedorBody.length
                ? totalAPagar
                : 0)
          ) || 0;

        return {
          cuit: grupo.cuit,
          razon_social: grupo.razon_social || "",
          cbu: grupo.cbu || grupo.documentos?.[0]?.cbu || "",
          id_banco:
            grupo.id_banco ?? grupo.documentos?.[0]?.id_banco ?? null,
          banco_nombre:
            grupo.banco_nombre || grupo.documentos?.[0]?.banco_nombre || "",
          total,
        };
      }),
    [proveedoresReales, preview, totalAPagar, pagosNoProveedorBody.length]
  );

  // Mantener medioPago alineado con la propuesta editable (paso Medio / Confirmación).
  useEffect(() => {
    if (modoEdicion) return;
    if (activeStep !== PASO.MEDIO && activeStep !== PASO.CONFIRMACION) return;
    if (!proveedoresConTotal.length) return;
    if (!Object.keys(configPagoPorProveedor || {}).length) return;

    setMedioPago((prev) =>
      armarMedioPagoAgregadoDesdeConfigs({
        configPorCuit: configPagoPorProveedor,
        proveedoresConTotal,
        medioPagoBase: prev,
        cuentasBancarias,
      })
    );
  }, [
    modoEdicion,
    activeStep,
    configPagoPorProveedor,
    proveedoresConTotal,
    cuentasBancarias,
  ]);

  const medioPagoValido = useMemo(() => {
    // Sin preview todavía no se puede validar/distribuir el total.
    if (totalAPagar == null || Number.isNaN(totalAPagar)) return false;
    if (!modoEdicion && !configsPagoOk) return false;

    const localOk = validarMedioPagoMulti(
      medioPago,
      totalAPagar,
      proveedoresConTotal,
      configPagoPorProveedor
    );
    if (!localOk) return false;

    // Lote multi-proveedor: el back es fuente de verdad para medios y cheques únicos.
    if (
      debeUsarLote &&
      proveedoresReales.length > 0 &&
      (activeStep === PASO.MEDIO || activeStep === PASO.CONFIRMACION)
    ) {
      if (previewMediosLoading) return false;
      if (previewMediosError) return false;
      if (previewMediosLote == null) return false;
      return Boolean(previewMediosLote.medios_lote_ok);
    }

    return true;
  }, [
    medioPago,
    totalAPagar,
    proveedoresConTotal,
    modoEdicion,
    configsPagoOk,
    configPagoPorProveedor,
    debeUsarLote,
    proveedoresReales.length,
    activeStep,
    previewMediosLoading,
    previewMediosError,
    previewMediosLote,
  ]);

  // Preview de medios del lote (validación back por proveedor + cheques únicos).
  useEffect(() => {
    if (modoEdicion) return;
    if (activeStep !== PASO.MEDIO && activeStep !== PASO.CONFIRMACION) {
      setPreviewMediosLote(null);
      setPreviewMediosError("");
      setPreviewMediosLoading(false);
      return;
    }
    if (!debeUsarLote || proveedoresReales.length === 0) {
      setPreviewMediosLote(null);
      setPreviewMediosError("");
      setPreviewMediosLoading(false);
      return;
    }
    if (!previewTieneResumen(preview) || previewLoading) return;
    if (!medioPago?.fechaPago) return;

    let activo = true;
    const timer = window.setTimeout(async () => {
      if (!activo) return;
      setPreviewMediosLoading(true);
      setPreviewMediosError("");

      try {
        const proveedoresItems = proveedoresReales.map((grupo) => {
          const cuitNorm = String(grupo.cuit || "").replace(/\D/g, "");
          try {
            const body = construirBodyProveedor(grupo, {
              confirmarAdvertencias: false,
              forPreview: true,
            });
            return {
              cuit: cuitNorm,
              comprobantes: body.comprobantes,
              medios_pago: body.medios_pago ?? [],
              ...(body.retenciones ? { retenciones: body.retenciones } : {}),
            };
          } catch {
            const comprobantes = grupo.documentos
              .filter((row) => !esPagoNoProveedor(row))
              .map((row) => mapFilaAComprobanteBody(row, importesAPagar));
            const previewGrupo =
              preview?.grupos?.find((g) => String(g.cuit) === String(grupo.cuit)) ??
              null;
            const totalProv =
              Number(previewGrupo?.resumen?.total_a_pagar ?? 0) || 0;
            const cfg = configPagoPorProveedor[String(grupo.cuit)];
            const medioPagoEscalado = cfg?.medio
              ? armarMedioPagoDesdeConfigProveedor({
                  config: cfg,
                  totalAPagar: totalProv,
                  medioPagoBase: medioPago,
                  cuentasBancarias,
                  cuit: grupo.cuit,
                })
              : escalarMedioPagoATotal(medioPago, totalProv, grupo.cuit);
            const cuentasProv = enriquecerCuentasOrigen(
              medioPagoEscalado.transferencia?.cuentasOrigen ?? [],
              cuentasBancarias
            );
            const medios_pago = armarMediosPagoBodySafe({
              medioPago: medioPagoEscalado,
              cuentasOrigenDetalle: cuentasProv,
              cuit: grupo.cuit,
            });
            const retenciones = retencionesBodyDesdeGrupo(previewGrupo, preview);
            return {
              cuit: cuitNorm,
              comprobantes,
              medios_pago,
              ...(retenciones ? { retenciones } : {}),
            };
          }
        });

        const bodyPreview = buildLotePreviewBody({
          fechaPago: moment(medioPago.fechaPago).format("YYYY-MM-DD"),
          observaciones: medioPago.observaciones,
          proveedoresItems,
          pagos_no_proveedor: pagosNoProveedorBody,
        });

        const data = await previewLoteOrdenPago(bodyPreview);
        if (!activo) return;
        setPreviewMediosLote(data);
      } catch (err) {
        if (!activo) return;
        setPreviewMediosLote(null);
        setPreviewMediosError(
          err.message || "No se pudo validar los medios del lote"
        );
      } finally {
        if (activo) setPreviewMediosLoading(false);
      }
    }, 450);

    return () => {
      activo = false;
      window.clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    modoEdicion,
    activeStep,
    debeUsarLote,
    proveedoresReales,
    preview,
    previewLoading,
    medioPago,
    configPagoPorProveedor,
    importesAPagar,
    pagosNoProveedorBody,
    cuentasBancarias,
  ]);

  // Al deseleccionar: medios a cero. Al cambiar el total: reescalar.
  useEffect(() => {
    if (filasSeleccionadas.length === 0) {
      setMedioPago((prev) => {
        // Ya en cero: no recrear el objeto (evita re-renders / loops).
        if (!hayMedioActivo(prev) && sumaImportesMedios(prev) < 0.01) {
          return prev;
        }
        return {
          ...crearMedioPagoInicial(prev.fechaPago || moment()),
          fechaPago: prev.fechaPago || moment(),
          observaciones: prev.observaciones || "",
          transferencia: { activo: false, cuentasOrigen: [] },
        };
      });
      return;
    }

    // Esperar el preview para no reescalar con un total viejo/null.
    if (previewLoading) return;
    if (totalAPagar == null || Number.isNaN(totalAPagar)) return;

    setMedioPago((prev) => {
      if (!requiereMediosPago(totalAPagar)) {
        if (!hayMedioActivo(prev)) return prev;
        return {
          ...prev,
          efectivo: { activo: false, importe: "" },
          transferencia: { activo: false, cuentasOrigen: [] },
          compensacion: {
            ...(prev.compensacion || {}),
            activo: false,
            importe: "",
            loading: false,
            error: null,
          },
          debitoAutomatico: {
            activo: false,
            importe: "",
            ultimos4: "",
            referencia: "",
          },
          tarjetaCredito: {
            activo: false,
            importe: "",
            ultimos4: "",
            banco: "",
            referencia: "",
          },
          ajuste: {
            activo: false,
            importe: "",
            concepto: prev.ajuste?.concepto,
            descripcion: "",
          },
          cheque: { activo: false, recibidos: [], emitidos: [] },
        };
      }

      if (!hayMedioActivo(prev)) return prev;
      return escalarMedioPagoATotal(prev, totalAPagar);
    });
  }, [filasSeleccionadas.length, totalAPagar, previewLoading]);

  const cuentasOrigenDetalle = useMemo(
    () =>
      enriquecerCuentasOrigen(
        medioPago.transferencia?.cuentasOrigen ?? [],
        cuentasBancarias
      ),
    [medioPago.transferencia?.cuentasOrigen, cuentasBancarias]
  );

  const cuitUnicoCompensacion = useMemo(() => {
    if (proveedoresReales.length !== 1) return "";
    return String(proveedoresReales[0]?.cuit || "").replace(/\D/g, "");
  }, [proveedoresReales]);

  useEffect(() => {
    // Solo al entrar al paso de medio / modo edición con transferencia
    // activa pero sin cuentas (estado inicial default). No reinyectar si el
    // usuario desactivó o vació a propósito.
    const enPasoMedio = modoEdicion || activeStep === PASO.MEDIO;
    if (!enPasoMedio) return;
    const resumen = obtenerResumenPreview(preview);
    if (!requiereMediosPago(resumen?.total_a_pagar)) return;
    if (!medioPago.transferencia?.activo) return;
    if (medioPago.transferencia?.cuentasOrigen?.length > 0) return;

    setMedioPago((prev) => {
      // Revalidar dentro del setState para no pisar un uncheck en curso.
      if (!prev.transferencia?.activo) return prev;
      if (prev.transferencia?.cuentasOrigen?.length > 0) return prev;
      return {
        ...prev,
        transferencia: {
          ...prev.transferencia,
          activo: true,
          cuentasOrigen: [nuevaFila(resumen.total_a_pagar)],
        },
      };
    });
    // Dependencias acotadas: no reejecutar al borrar la última cuenta.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeStep, modoEdicion, preview]);

  const handleMedioPagoChange = (partial) => {
    setMedioPago((prev) => {
      const next = { ...prev };
      Object.keys(partial || {}).forEach((clave) => {
        const valor = partial[clave];
        const esObjetoPlano =
          valor != null &&
          typeof valor === "object" &&
          !Array.isArray(valor) &&
          !(valor instanceof Date) &&
          !moment.isMoment(valor);
        if (esObjetoPlano) {
          // Merge profundo de medios; arrays (cuentasOrigen, cheques) se reemplazan.
          next[clave] = { ...(prev[clave] || {}), ...valor };
        } else {
          next[clave] = valor;
        }
      });
      return next;
    });
  };

  const handleSelectEmpresa = (emp) => {
    const id = idDeEmpresa(emp);
    const nombre = nombreDeEmpresa(emp);
    const cambio = String(id) !== String(idEmpresaSeleccionada);
    setIdEmpresaSeleccionada(id);
    setNombreEmpresaSeleccionada(nombre);
    // Siempre limpia lo operativo al (re)elegir empresa.
    setCuit("");
    setError("");
    setResultado(null);
    limpiarEstadoProveedor();
    if (cambio) {
      setFiltrosPendientes(filtrosPendientesIniciales());
      setFiltrosAplicados(filtrosPendientesIniciales());
    }
  };

  const handleAplicarFiltros = () => {
    setFiltrosAplicados({ ...filtrosPendientes });
  };

  const handleLimpiarFiltros = () => {
    const vacio = filtrosPendientesIniciales();
    setFiltrosPendientes(vacio);
    setFiltrosAplicados(vacio);
  };

  const handleSiguiente = () => {
    if (activeStep === PASO.EMPRESA && idEmpresaSeleccionada != null) {
      setActiveStep(PASO.COMPROBANTES);
    } else if (activeStep === PASO.COMPROBANTES && puedeAvanzarComprobantes) {
      if (cuitConsultado) setCuit(cuitConsultado);
      setMedioPago((prev) =>
        armarMedioPagoAgregadoDesdeConfigs({
          configPorCuit: configPagoPorProveedor,
          proveedoresConTotal,
          medioPagoBase: prev,
          cuentasBancarias,
        })
      );
      setActiveStep(PASO.MEDIO);
    } else if (activeStep === PASO.MEDIO && medioPagoValido) {
      setActiveStep(PASO.CONFIRMACION);
    }
  };

  const handleVolver = () => {
    if (modoEdicion) {
      navigate(
        idOrdenPagoEdicion
          ? `/ordenes_pago_proveedores/${idOrdenPagoEdicion}`
          : "/ordenes_pago_proveedores"
      );
      return;
    }
    // Cualquier "Volver" que deje el wizard en Empresa (o lo cruce): reset total.
    if (activeStep === PASO.COMPROBANTES) {
      volverASeleccionEmpresa();
      return;
    }
    if (activeStep === PASO.MEDIO || activeStep === PASO.CONFIRMACION) {
      // MEDIO/CONF → comprobantes: limpia medios (los comprobantes siguen).
      setMedioPago({
        ...medioPagoInicial(),
        transferencia: { activo: false, cuentasOrigen: [] },
      });
      setActiveStep((s) => s - 1);
      return;
    }
    if (activeStep > 0) {
      setActiveStep((s) => s - 1);
    } else {
      navigate(-1);
    }
  };

  const handleCerrarDialogMultiOp = () => {
    setDialogMultiOp(false);
    reiniciarWizard();
  };

  /**
   * Body del endpoint por CUIT para un proveedor de la selección. Es también
   * el contrato de cada item de POST /orden-pago/lotes.
   */
  const construirBodyProveedor = (
    grupo,
    { confirmarAdvertencias, email, forPreview = false } = {}
  ) => {
    const comprobantes = grupo.documentos
      .filter((row) => !esPagoNoProveedor(row))
      .map((row) => mapFilaAComprobanteBody(row, importesAPagar));

    const esOpUnica =
      proveedoresReales.length === 1 && pagosNoProveedorBody.length === 0;

    const previewGrupo =
      preview?.grupos?.find((g) => String(g.cuit) === String(grupo.cuit)) ??
      null;
    const totalProv =
      Number(
        previewGrupo?.resumen?.total_a_pagar ??
          (esOpUnica ? obtenerResumenPreview(preview)?.total_a_pagar : 0)
      ) || 0;

    const medioPagoEscalado = (() => {
      const cfg = configPagoPorProveedor[String(grupo.cuit)];
      if (cfg?.medio) {
        return armarMedioPagoDesdeConfigProveedor({
          config: cfg,
          totalAPagar: totalProv,
          medioPagoBase: medioPago,
          cuentasBancarias,
          cuit: grupo.cuit,
        });
      }
      return esOpUnica
        ? medioPago
        : escalarMedioPagoATotal(medioPago, totalProv, grupo.cuit);
    })();

    // Cheque/e-cheque: el detalle se completa en el paso Medio.
    if (
      !forPreview &&
      esMedioChequeProv(configPagoPorProveedor[String(grupo.cuit)]?.medio) &&
      cantidadChequesMedio(medioPagoEscalado, grupo.cuit) === 0
    ) {
      throw new Error(
        `Completá los cheques para ${grupo.razon_social || grupo.cuit} en el paso Medio de pago`
      );
    }

    const cuentasProv = enriquecerCuentasOrigen(
      medioPagoEscalado.transferencia?.cuentasOrigen ?? [],
      cuentasBancarias
    );

    return buildGenerarOrdenPagoBody({
      comprobantes,
      fechaPago: moment(medioPago.fechaPago).format("YYYY-MM-DD"),
      observaciones: medioPago.observaciones,
      medioPago: medioPagoEscalado,
      cuentasOrigenDetalle: cuentasProv,
      user: localStorage.getItem("user"),
      email,
      cuit: esOpUnica ? null : grupo.cuit,
      totalAPagar: totalProv,
      confirmarAdvertencias,
      retenciones: retencionesBodyDesdeGrupo(previewGrupo, preview),
    });
  };

  const handleConfirmarOrden = async (
    emailProveedor,
    { confirmarAdvertencias = false } = {}
  ) => {
    if (
      (proveedoresReales.length === 0 && pagosNoProveedorBody.length === 0) ||
      !previewTieneResumen(preview) ||
      generando
    ) {
      return;
    }

    // El back rechaza con 409 si no viene el flag: pedimos la confirmación antes.
    if (!confirmarAdvertencias && previewRequiereConfirmacion(preview)) {
      setModalConfirmarOpen(false);
      setConfirmacionRetencion({
        mensaje: "",
        advertencias: preview?.advertencias ?? [],
        excedente: excedenteRetencionPreview(preview),
        totalAPagar: obtenerResumenPreview(preview)?.total_a_pagar ?? null,
        email: emailProveedor,
      });
      return;
    }

    setGenerando(true);
    setProveedorConError(null);

    try {
      // OP pendiente: PUT completo (reemplaza comps/medios) y luego aprobar.
      if (modoEdicion && idOrdenPagoEdicion) {
        const grupo = proveedoresReales[0];
        if (!grupo?.documentos?.length) {
          throw new Error("Seleccione al menos un comprobante");
        }
        const comprobantes = grupo.documentos
          .filter((row) => !esPagoNoProveedor(row))
          .map((row) => mapFilaAComprobanteBody(row, importesAPagar));
        const totalProv =
          Number(obtenerResumenPreview(preview)?.total_a_pagar) || 0;
        const cuentasProv = enriquecerCuentasOrigen(
          medioPago.transferencia?.cuentasOrigen ?? [],
          cuentasBancarias
        );
        const body = buildGenerarOrdenPagoBody({
          comprobantes,
          fechaPago: moment(medioPago.fechaPago).format("YYYY-MM-DD"),
          observaciones: medioPago.observaciones,
          medioPago,
          cuentasOrigenDetalle: cuentasProv,
          user: localStorage.getItem("user"),
          email: emailProveedor,
          totalAPagar: totalProv,
          confirmarAdvertencias,
          retenciones: retencionesBodyDesdeGrupo(
            preview?.grupos?.[0] ?? preview,
            preview
          ),
        });

        await actualizarOrdenPago(idOrdenPagoEdicion, body);

        const data = await aprobarOrdenPago(idOrdenPagoEdicion, {
          user: localStorage.getItem("user"),
        });
        setModalConfirmarOpen(false);
        setConfirmacionRetencion(null);
        const op = data?.orden_pago ?? {};
        await swal({
          title: data?.ya_aprobada ? "Ya estaba aprobada" : "Orden aprobada",
          text:
            data?.msg ||
            (op.nro_op
              ? `OP ${op.nro_op} · ${etiquetaEstadoOp(op.estado, op.estado_label)}`
              : nroOpEdicion
                ? `OP ${nroOpEdicion} pasó a estado Generada`
                : "La orden quedó en estado Generada."),
          icon: "success",
          closeOnClickOutside: false,
        });
        navigate(`/ordenes_pago_proveedores/${idOrdenPagoEdicion}`, {
          replace: true,
        });
        return;
      }

      /**
       * Lote: varios proveedores, mezcla con pagos varios, o solo pagos varios.
       * El back reserva los pagos_no_proveedor con id_lote.
       */
      if (debeUsarLote) {
        const body = buildLoteOrdenPagoBody({
          user: localStorage.getItem("user"),
          fechaPago: moment(medioPago.fechaPago).format("YYYY-MM-DD"),
          observaciones: medioPago.observaciones,
          proveedores: proveedoresReales.map((grupo) => ({
            cuit: grupo.cuit,
            body: construirBodyProveedor(grupo, { confirmarAdvertencias }),
          })),
          pagos_no_proveedor: pagosNoProveedorBody,
        });

        const data = await crearLoteOrdenPago(body);
        setModalConfirmarOpen(false);
        setConfirmacionRetencion(null);

        await swal({
          title: "Lote de órdenes de pago creado",
          text: mensajeLoteGenerado(data),
          icon: "success",
          closeOnClickOutside: false,
        });
        // Vuelve al paso Empresa para armar otra operación.
        reiniciarWizard();
        return;
      }

      const grupo = proveedoresReales[0];
      const data = await generarOrdenPago(
        grupo.cuit,
        construirBodyProveedor(grupo, {
          confirmarAdvertencias,
          email: emailProveedor,
        })
      );

      setModalConfirmarOpen(false);
      setConfirmacionRetencion(null);

      // Un solo proveedor puede seguir generando varias OP (una por banco origen).
      if (esGeneracionMultiOp(data)) {
        setResultadoGeneracion(data);
        setDialogMultiOp(true);
      } else {
        const estadoCrea =
          data?.orden_pago?.estado ?? data?.ordenes_pago?.[0]?.orden_pago?.estado;
        await swal({
          title:
            estadoCrea === "pendiente_aprobacion"
              ? "Orden de pago creada"
              : "Orden de pago generada",
          text: mensajeOrdenGenerada(data),
          icon: "success",
          closeOnClickOutside: false,
        });
        // Vuelve al paso Empresa para armar otra operación.
        reiniciarWizard();
      }
    } catch (err) {
      const msgChequeDup = mensajeErrorChequeDuplicadoLote(err);
      if (msgChequeDup) {
        setModalConfirmarOpen(false);
        setConfirmacionRetencion(null);
        swal({
          title: "Cheque duplicado en el lote",
          text: msgChequeDup,
          icon: "error",
          closeOnClickOutside: false,
        });
        return;
      }

      if (err?.status === 409 && err?.codigo === "retencion_excede_pago") {
        setModalConfirmarOpen(false);
        setConfirmacionRetencion({
          mensaje: err.message || "",
          advertencias: err.advertencias ?? [],
          excedente:
            Number(err.resumen?.excedente_retencion) ||
            excedenteRetencionPreview(preview),
          totalAPagar: err.resumen?.total_a_pagar ?? null,
          email: emailProveedor,
        });
        return;
      }

      // El lote es todo o nada: se marca el proveedor que lo hizo fallar.
      if (err?.proveedorError) {
        const cuitFallido = String(err.proveedorError.cuit || "").replace(
          /\D/g,
          ""
        );
        const grupoFallido =
          proveedoresReales.find((p) => p.cuit === cuitFallido) ?? null;
        setModalConfirmarOpen(false);
        setConfirmacionRetencion(null);
        setProveedorConError({
          cuit: cuitFallido || grupoFallido?.cuit || "",
          razon_social: grupoFallido?.razon_social || "",
          mensaje:
            err.proveedorError.msg ||
            err.proveedorError.message ||
            err.proveedorError.error ||
            err.message ||
            "",
        });
        swal({
          title: "No se generó el lote",
          text: mensajeErrorLote(err, proveedoresReales),
          icon: "error",
          closeOnClickOutside: false,
        });
        return;
      }

      swal({
        title: modoEdicion
          ? "No se pudo guardar / aprobar la orden de pago"
          : debeUsarLote
            ? "No se pudo generar el lote"
            : "No se pudo generar la orden de pago",
        text: normalizarMensajeErrorPreview(
          err.message ||
            (modoEdicion
              ? "No se pudo actualizar o aprobar la orden de pago"
              : "No se pudo generar la orden de pago")
        ),
        icon: "error",
        closeOnClickOutside: false,
      });
    } finally {
      setGenerando(false);
    }
  };

  const handleAceptarRetencionExcede = () => {
    const datos = confirmacionRetencion;
    if (!datos) return;
    handleConfirmarOrden(datos.email, { confirmarAdvertencias: true });
  };

  const handleConfirmarAdelanto = async (cuitProv, body) => {
    if (generandoAdelanto) return;
    setGenerandoAdelanto(true);
    try {
      const data = await crearAdelantoProveedor(cuitProv, body);
      setModalAdelantoOpen(false);

      const nroOp =
        data?.orden_pago?.nro_op ??
        data?.ordenes_pago?.[0]?.orden_pago?.nro_op ??
        data?.ordenes_pago?.[0]?.nro_op;

      await swal({
        title: "Adelanto creado",
        text: nroOp
          ? `Se generó la OP ${nroOp}. Quedará como saldo a favor del proveedor.`
          : "Se generó la orden de pago. Quedará como saldo a favor del proveedor.",
        icon: "success",
        closeOnClickOutside: false,
      });

      await cargarPendientes(filtrosAplicados);
    } catch (err) {
      swal({
        title: "No se pudo generar el adelanto",
        text: normalizarMensajeErrorPreview(
          err.message || "No se pudo generar el adelanto"
        ),
        icon: "error",
        closeOnClickOutside: false,
      });
    } finally {
      setGenerandoAdelanto(false);
    }
  };

  const enOrden = activeStep >= PASO.MEDIO;
  const tituloPantalla = modoEdicion
    ? `Aprobar orden de pago${nroOpEdicion ? ` · ${nroOpEdicion}` : ""}`
    : enOrden
      ? "Orden de Pago a Proveedores"
      : "Pagos a Proveedores";

  const handleAnularOpEdicion = async (motivo) => {
    if (!idOrdenPagoEdicion) return;
    setAnulando(true);
    try {
      const data = await anularOrdenPago(idOrdenPagoEdicion, {
        user: localStorage.getItem("user"),
        motivo,
      });
      setModalAnularOpen(false);

      if (data?.ya_anulada) {
        await swal({
          title: "Ya estaba anulada",
          text: data?.msg || "La orden de pago ya se encontraba anulada.",
          icon: "info",
          closeOnClickOutside: false,
        });
      } else {
        await swal({
          title: "Orden anulada",
          text:
            data?.msg ||
            "Se liberaron las facturas y quedaron disponibles otra vez.",
          icon: "success",
          closeOnClickOutside: false,
        });
      }

      navigate(`/ordenes_pago_proveedores/${idOrdenPagoEdicion}`, {
        replace: true,
      });
    } catch (err) {
      swal({
        title: "No se pudo anular",
        text: mensajeErrorAnularOp(err),
        icon: "error",
        closeOnClickOutside: false,
      });
    } finally {
      setAnulando(false);
    }
  };

  return (
    <ThemeProvider theme={temaPagos}>
      <CssBaseline />
      <div
        style={{
          minHeight: "100vh",
          backgroundColor: idEmpresaSeleccionada
            ? `${colorEmpresa}12`
            : "#FAFBFC",
          transition: "background-color 0.35s ease",
        }}
      >
        <Navbar />
        <Box
          sx={{
            background: idEmpresaSeleccionada
              ? `linear-gradient(135deg, ${colorEmpresa} 0%, rgba(0,0,0,0.22) 100%)`
              : `linear-gradient(135deg, ${COLOR_EMPRESA_DEFAULT} 0%, rgba(0,0,0,0.15) 100%)`,
            backgroundColor: colorEmpresa,
            color: "#fff",
            py: 1.5,
            px: 2,
            position: "relative",
            overflow: "hidden",
            transition: "background 0.35s ease, background-color 0.35s ease",
            "&::after": {
              content: '""',
              position: "absolute",
              top: 0,
              right: 0,
              bottom: 0,
              left: 0,
              background:
                "radial-gradient(circle at top right, rgba(255,255,255,0.12), transparent 40%)",
              pointerEvents: "none",
            },
          }}
        >
          <Container maxWidth="xl" sx={{ position: "relative", zIndex: 1 }}>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={1}
              alignItems={{ xs: "flex-start", sm: "center" }}
              justifyContent="space-between"
            >
              <Stack direction="row" spacing={0.5} alignItems="center">
                <Tooltip
                  title={
                    modoEdicion
                      ? "Volver al detalle"
                      : activeStep === PASO.EMPRESA
                        ? "Volver"
                        : activeStep === PASO.COMPROBANTES
                          ? "Volver a empresas"
                          : "Volver"
                  }
                >
                  <IconButton
                    onClick={handleVolver}
                    aria-label="Volver"
                    size="small"
                    sx={{
                      color: "#fff",
                      mr: 0.5,
                      bgcolor: "rgba(255,255,255,0.12)",
                      "&:hover": { bgcolor: "rgba(255,255,255,0.22)" },
                    }}
                  >
                    <ArrowBackIcon />
                  </IconButton>
                </Tooltip>
                <Typography variant="h5" component="h1" fontWeight={700}>
                  {tituloPantalla}
                </Typography>
              </Stack>
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                {modoEdicion ? (
                  <Chip
                    label="Pendiente de aprobación"
                    size="small"
                    sx={{
                      bgcolor: "rgba(255,255,255,0.18)",
                      color: "#fff",
                      fontWeight: 600,
                    }}
                  />
                ) : null}
                {nombreEmpresaSeleccionada ? (
                  <Chip
                    label={nombreEmpresaSeleccionada}
                    size="small"
                    sx={{
                      bgcolor: "rgba(255,255,255,0.18)",
                      color: "#fff",
                      fontWeight: 600,
                    }}
                  />
                ) : null}
                {(enOrden || modoEdicion) && (
                  <Chip
                    icon={<CalendarTodayIcon sx={{ color: "#fff !important" }} />}
                    label={fechaHoy}
                    size="small"
                    sx={{
                      bgcolor: "rgba(255,255,255,0.18)",
                      color: "#fff",
                      "& .MuiChip-icon": { color: "#fff" },
                    }}
                  />
                )}
              </Stack>
            </Stack>
          </Container>
        </Box>
        <Container
          maxWidth="xl"
          sx={{ pt: "20px", pb: "100px", px: 2, position: "relative", zIndex: 1 }}
        >
          {!modoEdicion ? <OrdenPagoStepper activeStep={activeStep} /> : null}

          {activeStep === PASO.EMPRESA && !modoEdicion && (
            <TabSeleccionEmpresa
              empresas={empresas}
              loading={loadingEmpresas}
              error={errorEmpresas}
              idEmpresaSeleccionada={idEmpresaSeleccionada}
              onSelect={handleSelectEmpresa}
              onContinuar={handleSiguiente}
            />
          )}

          {modoEdicion ? (
            <>
              <Box sx={{ position: "relative" }}>
                {previewLoading ? (
                  <Box
                    sx={{
                      position: "sticky",
                      top: 72,
                      zIndex: 12,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 1.5,
                      py: 1.25,
                      mb: 1.5,
                      px: 2,
                      bgcolor: "rgba(255,255,255,0.95)",
                      border: "1px solid",
                      borderColor: "divider",
                      borderRadius: 1,
                      boxShadow: 1,
                    }}
                  >
                    <CircularProgress size={22} />
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      fontWeight={600}
                    >
                      Actualizando montos y retenciones…
                    </Typography>
                  </Box>
                ) : null}
                <Box
                  sx={{
                    opacity: previewLoading ? 0.7 : 1,
                    transition: "opacity 0.15s ease",
                  }}
                >
                  <TabMedioPago
                    comprobantesTabla={comprobantesTabla}
                    preview={preview}
                    previewLoading={previewLoading}
                    previewError={previewError}
                    medioPago={medioPago}
                    onMedioPagoChange={handleMedioPagoChange}
                    cuentasBancarias={cuentasBancarias}
                    loadingCuentas={loadingCuentas}
                    cuitProveedor={cuitUnicoCompensacion}
                    compensacionHabilitada={
                      proveedoresReales.length === 1 &&
                      pagosNoProveedorBody.length === 0
                    }
                    proveedores={proveedoresConTotal}
                    mostrarComprobantes
                    comprobantesEditables
                    importesAPagar={importesAPagar}
                    onImportesAPagarChange={handleImportesAPagarChange}
                    onQuitarComprobante={handleQuitarComprobante}
                    cantidadSeleccionada={filasSeleccionadas.length}
                    filasSeleccionadas={filasSeleccionadas}
                    onEditarRetencion={handleEditarRetencion}
                  />
                </Box>
              </Box>
              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 2,
                  justifyContent: "space-between",
                  mt: 4,
                  pt: 2,
                  borderTop: "1px solid #e0e0e0",
                }}
              >
                <Button
                  variant="outlined"
                  startIcon={<ArrowBackIcon />}
                  onClick={handleVolver}
                  disabled={previewLoading || generando || anulando}
                >
                  Volver
                </Button>
                <Stack direction="row" spacing={1} flexWrap="wrap">
                  <Button
                    variant="outlined"
                    color="error"
                    startIcon={
                      anulando ? (
                        <CircularProgress size={18} color="inherit" />
                      ) : (
                        <BlockIcon />
                      )
                    }
                    disabled={previewLoading || generando || anulando}
                    onClick={() => setModalAnularOpen(true)}
                  >
                    Anular
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={
                      generando || previewLoading ? (
                        <CircularProgress size={18} color="inherit" />
                      ) : (
                        <CheckCircleOutlineIcon />
                      )
                    }
                    disabled={
                      generando ||
                      anulando ||
                      !puedeAvanzarComprobantes ||
                      !medioPagoValido ||
                      previewLoading ||
                      Boolean(previewError)
                    }
                    onClick={() => setModalConfirmarOpen(true)}
                  >
                    {previewLoading ? "Calculando…" : "Aprobar"}
                  </Button>
                </Stack>
              </Box>
            </>
          ) : null}

          {!modoEdicion && activeStep === PASO.COMPROBANTES && (
            <>
              <ListadoPendientesEmpresa
                filtros={filtrosPendientes}
                onFiltrosChange={setFiltrosPendientes}
                onAplicarFiltros={handleAplicarFiltros}
                onLimpiarFiltros={handleLimpiarFiltros}
                filtrosAplicados={resultado?.filtros || filtrosAplicados}
                idEmpresa={idEmpresaSeleccionada}
                imputaciones={imputaciones}
                proveedores={proveedores}
                resumen={resultado?.resumen}
                loading={loading}
                error={error}
                rowSelection={rowSelection}
                onRowSelectionChange={setRowSelection}
                importesAPagar={importesAPagar}
                onImportesAPagarChange={handleImportesAPagarChange}
                preview={preview}
                previewLoading={previewLoading}
                previewError={previewError}
                filasSeleccionadas={filasSeleccionadas}
                puedeContinuar={puedeAvanzarComprobantes}
                onContinuar={handleSiguiente}
                onAbrirAdelanto={() => setModalAdelantoOpen(true)}
                labelContinuar="Siguiente"
                configPagoPorProveedor={configPagoPorProveedor}
                onConfigPagoPorProveedorChange={setConfigPagoPorProveedor}
                cuentasBancarias={cuentasBancarias}
                loadingCuentas={loadingCuentas}
              />
              <Box sx={{ mt: 3 }}>
                <Button
                  variant="outlined"
                  startIcon={<ArrowBackIcon />}
                  onClick={handleVolver}
                >
                  Volver
                </Button>
              </Box>
            </>
          )}

          {!modoEdicion && activeStep === PASO.MEDIO && preview && (
            <TabMedioPago
              comprobantesTabla={comprobantesTabla}
              preview={preview}
              previewLoading={previewLoading}
              previewError={previewError}
              previewMediosLote={previewMediosLote}
              previewMediosLoading={previewMediosLoading}
              previewMediosError={previewMediosError}
              medioPago={medioPago}
              onMedioPagoChange={handleMedioPagoChange}
              cuentasBancarias={cuentasBancarias}
              loadingCuentas={loadingCuentas}
              cuitProveedor={cuitUnicoCompensacion}
              compensacionHabilitada={
                proveedoresReales.length === 1 &&
                pagosNoProveedorBody.length === 0
              }
              proveedores={proveedoresConTotal}
              configPagoPorProveedor={configPagoPorProveedor}
              onConfigPagoChange={handleConfigPagoProveedorChange}
              onEditarRetencion={handleEditarRetencion}
              comprobantesEditables
              importesAPagar={importesAPagar}
              onImportesAPagarChange={handleImportesAPagarChange}
              onQuitarComprobante={handleQuitarComprobante}
              cantidadSeleccionada={filasSeleccionadas.length}
              filasSeleccionadas={filasSeleccionadas}
            />
          )}

          {!modoEdicion && activeStep === PASO.CONFIRMACION && preview && (
            <TabConfirmacion
              comprobantesTabla={comprobantesTabla}
              preview={preview}
              previewLoading={previewLoading}
              previewError={previewError}
              previewMediosLote={previewMediosLote}
              previewMediosLoading={previewMediosLoading}
              previewMediosError={previewMediosError}
              medioPago={medioPago}
              cuentasOrigenDetalle={cuentasOrigenDetalle}
              proveedores={proveedoresConTotal}
              configPagoPorProveedor={configPagoPorProveedor}
              onConfigPagoChange={handleConfigPagoProveedorChange}
              cuentasBancarias={cuentasBancarias}
              loadingCuentas={loadingCuentas}
              proveedorConError={proveedorConError}
            />
          )}

          {!modoEdicion && activeStep >= PASO.MEDIO && (
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 2,
                justifyContent: "space-between",
                mt: 4,
                pt: 2,
                borderTop: "1px solid #e0e0e0",
              }}
            >
              <Button
                variant="outlined"
                startIcon={<ArrowBackIcon />}
                onClick={handleVolver}
              >
                Volver
              </Button>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                {activeStep === PASO.MEDIO && (
                  <Button
                    variant="contained"
                    endIcon={<ArrowForwardIcon />}
                    disabled={
                      !medioPagoValido ||
                      previewMediosLoading ||
                      Boolean(previewMediosError)
                    }
                    onClick={handleSiguiente}
                  >
                    {previewMediosLoading ? "Validando medios…" : "Siguiente"}
                  </Button>
                )}
                {activeStep === PASO.CONFIRMACION && (
                  <Button
                    variant="contained"
                    startIcon={
                      generando ? (
                        <CircularProgress size={18} color="inherit" />
                      ) : (
                        <CheckCircleOutlineIcon />
                      )
                    }
                    disabled={
                      generando ||
                      !medioPagoValido ||
                      previewLoading ||
                      previewMediosLoading ||
                      Boolean(previewError) ||
                      Boolean(previewMediosError)
                    }
                    onClick={() => setModalConfirmarOpen(true)}
                  >
                    {previewMediosLoading
                      ? "Validando medios…"
                      : "Confirmar orden de pago"}
                  </Button>
                )}
              </Box>
            </Box>
          )}

        </Container>
        <ResultadoGeneracionMultiOp
          open={dialogMultiOp}
          data={resultadoGeneracion}
          onClose={handleCerrarDialogMultiOp}
        />
        <ModalConfirmarOrdenPago
          open={modalConfirmarOpen}
          onClose={() => !generando && setModalConfirmarOpen(false)}
          onConfirm={handleConfirmarOrden}
          cuit={cuitConsultado}
          razonSocial={razonSocial}
          emailInicial={resultado?.email || resultado?.correo || ""}
          generando={generando}
          cantidadProveedores={cantidadProveedoresSeleccionados}
          modo={modoEdicion ? "aprobar" : undefined}
        />
        {modoEdicion ? (
          <ModalAnularOrdenPago
            open={modalAnularOpen}
            onClose={() => {
              if (!anulando) setModalAnularOpen(false);
            }}
            onConfirm={handleAnularOpEdicion}
            nroOp={nroOpEdicion || ""}
            anulando={anulando}
          />
        ) : null}
        <ModalRetencionExcedePago
          open={Boolean(confirmacionRetencion)}
          onClose={() => !generando && setConfirmacionRetencion(null)}
          onConfirmar={handleAceptarRetencionExcede}
          generando={generando}
          mensaje={confirmacionRetencion?.mensaje}
          advertencias={confirmacionRetencion?.advertencias ?? []}
          excedente={confirmacionRetencion?.excedente ?? 0}
          totalAPagar={confirmacionRetencion?.totalAPagar ?? null}
        />
        <ModalCrearAdelanto
          open={modalAdelantoOpen}
          onClose={() => !generandoAdelanto && setModalAdelantoOpen(false)}
          onConfirm={handleConfirmarAdelanto}
          proveedores={proveedores}
          idEmpresa={idEmpresaSeleccionada}
          generando={generandoAdelanto}
        />
      </div>
    </ThemeProvider>
  );
};

export default OrdenPagoWizard;
